import { safe, isNullOrUndefined } from "./";
import { IDataEntity } from "../models";
import * as moment from "moment";

export function getPerformanceForCompare(entityCollection: IDataEntity[], start, end) {
	let chartData = [];
	let labels = [];
	let montlhyPerformance = [];
	let earliestShareDate;
	let shareClassCurrency;
	let initialShareClass = true;
	for (let entity of entityCollection) {
		if (entity.type === "CLSS") {
			shareClassCurrency = entity.properties.shareclass_currency_code;
			let label = {
				label: `${entity.properties['fund_name']} ${entity.properties['shareclass_name']}`,
				identifier: entity.clientCode
			};
			labels.push(label);
			const shareClassChartData = getChartDataFromRange(entity, "morning_star_daily_prices", "morning_star_monthly_perf", start, end);
			if (initialShareClass) { // so that earliestShareDate is only set from the first CLSS entity passed through in entityCollection i.e. the fund page's share class
				earliestShareDate = safe(() => shareClassChartData[0].date);
				initialShareClass = false;
			}
			montlhyPerformance.push(shareClassChartData);
		} else if (entity.clientCode.toUpperCase().includes("CBLT")) {
			labels.push({ label: entity.properties.name, identifier: entity.clientCode });
			if (entity.properties.is_benchmark) {
				const indexChartData = getChartDataFromRange(entity, "morning_star_daily_benchmark", "mstar_monthly_benchmark", start, end, shareClassCurrency);
				const earliestIndexDate = safe(() => indexChartData[0].date, null);

				if (moment.utc(earliestIndexDate).isSameOrBefore(moment.utc(earliestShareDate)) || indexChartData.length === 0) {
					montlhyPerformance.push(indexChartData);
				}
			}
		} else {
			labels.push({ label: entity.properties.name, identifier: entity.clientCode });
			if (entity.properties.is_benchmark) {
				const indexChartData = getChartDataFromRange(entity, "morning_star_daily_benchmark", "mstar_monthly_benchmark_gbp", start, end);
				const earliestIndexDate = safe(() => indexChartData[0].date, null);
				if (moment.utc(earliestIndexDate).isSameOrBefore(moment.utc(earliestShareDate)) || indexChartData.length === 0) {
					montlhyPerformance.push(indexChartData);
				}
			} else {
				const sectorChartData = getChartDataFromRange(entity, "", "mstar_monthly_sector_gbp", start, end);
				const earliesSectorDate = safe(() => sectorChartData[0].date, null);
				if (moment.utc(earliesSectorDate).isSameOrBefore(moment.utc(earliestShareDate)) || sectorChartData.length === 0) {
					montlhyPerformance.push(sectorChartData);
				}
			}
		}
	};

	let shareGrowthStart = 100;
	let shareData = montlhyPerformance.shift();
	let growthStartValues = montlhyPerformance.map(item => 100);
	for (let shareIndex = 0; shareIndex < shareData.length; shareIndex++) {
		let dataObj = {};
		let shareDate = shareData[shareIndex].date;
		if (shareIndex === 0) {
			let firstObject = labels.reduce((acc, item) => {
				if (isNullOrUndefined(acc.date)) {
					acc.date = moment(shareDate).startOf("month").format("YYYY-MM-DD");
				}
				acc[item.label] = 100;

				return acc;
			}, {});

			chartData.push(firstObject);
		}
		if (!isNullOrUndefined(shareData[shareIndex])) {
			let shareValue = rebaseDataPoint(shareGrowthStart, parseFloat(shareData[shareIndex].percent.toString()));
			shareGrowthStart = shareValue;
			Object.assign(dataObj, {
				date: shareDate,
				[labels[0].label]: shareValue,
			});
		};

		for (let index = 0; index < montlhyPerformance.length; index++) {
			let [entityObj] = montlhyPerformance[index].filter(item => {
				return shareDate === item.date;
			});
			if (!isNullOrUndefined(entityObj)) {
				let value = rebaseDataPoint(growthStartValues[index], parseFloat(entityObj.percent.toString()));
				growthStartValues[index] = value;
				Object.assign(dataObj, {
					[labels[index + 1].label]: value
				});
			}
		}

		chartData.push(dataObj);
	}

	// tslint:disable-next-line:variable-name
	let YTDData = entityCollection.map(entity => {
		if (entity.type === "CLSS") {
			return getYTData(entity, "morning_star_ytd_perf");
		} else {
			if (entity.properties.is_benchmark) {
				if (entity.clientCode.toUpperCase().includes("CBLT")) {
					return getYTData(entity, "mstar_ytd_benchmark", shareClassCurrency);
				} else {
					return getYTData(entity, "mstar_ytd_benchmark_gbp");
				}
			} else {
				return getYTData(entity, "mstar_ytd_sector_gbp");
			}
		}
	});
	let trailingData = entityCollection.map(entity => {
		if (entity.type === "CLSS") {
			return getTrailingData(entity, "morning_star_yearly_perf");
		} else {
			if (entity.properties.is_benchmark) {
				if (entity.clientCode.toUpperCase().includes("CBLT")) {
					return getTrailingData(entity, "mstar_yearly_benchmark", shareClassCurrency);
				} else {
					return getTrailingData(entity, "mstar_yearly_benchmark_gbp");
				}
			} else {
				return getTrailingData(entity, "mstar_yearly_sector_gbp");
			}
		}
	});
	let latestDateForDisplay = moment(YTDData[0][0].end_date).format('MMMM');
	let latestDateForDisplayComplete = moment(YTDData[0][0].end_date).format('DD MMMM YYYY');

	return {
		labels,
		chartData,
		YTDData,
		trailingData,
		latestDateForDisplay,
		latestDateForDisplayComplete,
	};
}

function getChartDataFromRange(entity, dailyCode, monthlyCode, startDate, endDate, ccy = "GBP") {
	let chartData = [].concat(safe(() => entity.timeseries.filter(t => t.code === monthlyCode && t.ccy === ccy)[0].values, []));
	chartData = chartData
		.map(item => ({ ...item, date: moment(item.date).format("YYYY-MM-DD") }))
		.sort((a, b) => {
			let aDate = new Date(a.date as any).getTime();
			let bDate = new Date(b.date as any).getTime();

			return aDate - bDate;
		})
		.filter(item => moment(item.date).isBetween(startDate, endDate, null, '[]'));

	if (moment().isSame(endDate, 'month')) {
		let dailyData = safe(() => entity.timeseries.filter(t => t.code === dailyCode)[0].values, []);
		if (dailyData && dailyData.length > 0) {
			dailyData = dailyData.sort((a, b) => {
				let aDate = new Date(a.date as any).getTime();
				let bDate = new Date(b.date as any).getTime();

				return aDate - bDate;
			});
			let lastDaily = dailyData[dailyData.length - 1];
			let lastChartData = chartData[chartData.length - 1] || 0;
			let lastMonthEndDaily = dailyData.filter(d => d["date"] == lastChartData["date"]);
			if(lastMonthEndDaily.length > 0){
				let changeSinceLastMonthEnd = ((lastDaily["value"] - lastMonthEndDaily[0]["value"])/lastDaily["value"]) * 100;
				chartData.push(Object.assign(lastDaily, 
					{ 
						percent: changeSinceLastMonthEnd 
					}
				));
			}
		}
	}

	return chartData;
}

function getChartDataFromLimit(shareClass, dailyCode, monthlyCode, limit) {
	let chartData = [].concat(safe(() => shareClass.timeseries.filter(t => t.code === monthlyCode)[0].values, []));
	chartData = chartData.sort((a, b) => {
		let aDate = new Date(a.date as any).getTime();
		let bDate = new Date(b.date as any).getTime();

		return aDate - bDate;
	});

	chartData = chartData.slice(chartData.length - limit, chartData.length);
	let dailyData = safe(() => shareClass.timeseries.filter(t => t.code === dailyCode)[0].values, []);
	if (dailyData && dailyData.length > 0) {
		dailyData = dailyData.sort((a, b) => {
			let aDate = new Date(a.date as any).getTime();
			let bDate = new Date(b.date as any).getTime();

			return aDate - bDate;
		});
		let lastDaily = dailyData[dailyData.length - 1];
		let lastChartData = chartData[chartData.length - 1] || 0;
		chartData.push(Object.assign(lastDaily, { percent: lastDaily.change, value: lastChartData.value * ((lastDaily['change_from_last_day'] as number / 100) + 1) }));
	}

	return chartData;
}

function rebaseDataPoint(start, dataPoint) {
	return (start * ((dataPoint / 100) + 1));
}

function getYTData(shareClass, code, ccy = "GBP") {
	let yearToYearData = safe(() => shareClass.statistics.filter(s => s.code === code && s.ccy === ccy)[0].values, null) as any as { start_date: string; end_date: string; value: number; label: string; }[];
	if (!yearToYearData) {
		yearToYearData = [];
	}

	let lengthLimit = 5;
	if (yearToYearData.length > lengthLimit) {
		yearToYearData = yearToYearData.slice(0, lengthLimit);
	}
	if (yearToYearData.length < 5) {
		let lastDate = safe(() => yearToYearData[yearToYearData.length - 1].start_date, moment().startOf('month').format('YYYY-MM-DD'));
		while (yearToYearData.length < 5) {
			let end = moment(lastDate).subtract(1, 'day').format('YYYY-MM-DD');
			let start = moment(lastDate).subtract(1, 'year').format('YYYY-MM-DD');
			lastDate = start;
			yearToYearData.push({
				label: '',
				value: 0,
				start_date: start,
				end_date: end
			});
		}
	}
	return yearToYearData;
}

function getTrailingData(shareClass, code, ccy = "GBP") {
	let trailingTypes = ['M12', 'M24', 'M36', 'M48', 'M60'];
	let trailingData = safe(() => shareClass.statistics.filter(s => s.code === code && s.ccy === ccy)[0].values, null) as any as { date: string; value: number; label: string; }[];
	if (!trailingData) {
		trailingData = [];
	}
	let trailingDataObj = trailingData.reduce((acc, current) => {
		acc[current.label] = current;
		return acc;
	}, {});
	let trailingDisplayData = [];
	let year = parseFloat(moment(safe(() => trailingData[0].date, undefined)).format('YYYY'));
	for (let trailingType of trailingTypes) {
		let current = trailingDataObj[trailingType];
		if (!current) {
			current = { label: trailingType, value: 0 };
		}
		current.year = year;
		year--;
		trailingDisplayData.push(current);
	}

	return trailingDisplayData;
}



// WEBPACK FOOTER //
// ./src/client/utils/performance-helper.ts